import { useEffect, useState } from "react";
import { Admin } from "@/lib/api";
import { AdminLayout } from "./AdminPages";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Pencil, Eye, EyeOff } from "lucide-react";
import { confirmDialog, toast } from "@/components/ui/dialog";

interface Announcement {
  id?: number;
  placement: "marquee" | "popup" | "sidebar";
  title: string;
  content_text: string;
  content_html: string;
  image_url: string;
  link_url: string;
  priority: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  popup_frequency: "always" | "once_per_session" | "once_per_day";
}

const EMPTY: Announcement = {
  placement: "marquee",
  title: "",
  content_text: "",
  content_html: "",
  image_url: "",
  link_url: "",
  priority: 0,
  is_active: true,
  starts_at: null,
  ends_at: null,
  popup_frequency: "once_per_day",
};

const PLACEMENTS = [
  { v: "marquee", label: "顶部滚动条幅", desc: "纯文本，循环滚动" },
  { v: "popup", label: "首页弹窗广告", desc: "图文混排，每访客至少每日一次" },
  { v: "sidebar", label: "侧栏常驻广告", desc: "图文混排，桌面端右下角" },
] as const;

export function AdminAnnouncementsPage() {
  const [list, setList] = useState<Announcement[]>([]);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [msg, setMsg] = useState("");

  const load = () => {
    Admin.announcements().then(setList).catch(() => setList([]));
  };
  useEffect(load, []);

  const save = async () => {
    if (!editing) return;
    setMsg("");
    try {
      await Admin.saveAnnouncement(editing.id || null, {
        ...editing,
        starts_at: editing.starts_at || null,
        ends_at: editing.ends_at || null,
      });
      setEditing(null);
      load();
    } catch (e: any) {
      setMsg("× " + (e?.message || "保存失败"));
    }
  };

  const remove = async (id: number) => {
    if (!(await confirmDialog({ title: "删除该公告？", description: "删除后不可恢复。", destructive: true, confirmText: "删除" }))) return;
    await Admin.delAnnouncement(id);
    toast.success("已删除");
    load();
  };

  if (editing) {
    const isMarquee = editing.placement === "marquee";
    return (
      <AdminLayout>
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold">{editing.id ? "编辑公告" : "新建公告"}</h1>
              <Button variant="ghost" onClick={() => setEditing(null)}>取消</Button>
            </div>

            <div className="space-y-1.5">
              <Label>展示位置</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {PLACEMENTS.map((p) => (
                  <button key={p.v} type="button"
                    onClick={() => setEditing({ ...editing, placement: p.v as any })}
                    className={`text-left rounded-lg border p-3 transition ${editing.placement === p.v ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:bg-muted/50"}`}>
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>标题</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="可选，弹窗/侧栏会展示" />
              </div>
              <div className="space-y-1.5">
                <Label>优先级（越大越靠前）</Label>
                <Input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: +e.target.value })} />
              </div>
            </div>

            {isMarquee ? (
              <div className="space-y-1.5">
                <Label>滚动文本（纯文本）</Label>
                <Textarea rows={3} value={editing.content_text}
                  onChange={(e) => setEditing({ ...editing, content_text: e.target.value })}
                  placeholder="平台升级公告：新增 AI 智能匹配功能…" />
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>HTML 富文本内容</Label>
                  <Textarea rows={6} className="font-mono text-xs" value={editing.content_html}
                    onChange={(e) => setEditing({ ...editing, content_html: e.target.value })}
                    placeholder="<p>支持 <strong>HTML</strong> 标签</p>" />
                  {editing.content_html && (
                    <div className="rounded border p-3 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-2">预览：</div>
                      <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: editing.content_html }} />
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>配图 URL</Label>
                    <Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://…/banner.png" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>跳转链接</Label>
                    <Input value={editing.link_url} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })} placeholder="https://…" />
                  </div>
                </div>
              </>
            )}

            {editing.placement === "popup" && (
              <div className="space-y-1.5">
                <Label>弹出频率</Label>
                <select className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={editing.popup_frequency}
                  onChange={(e) => setEditing({ ...editing, popup_frequency: e.target.value as any })}>
                  <option value="once_per_day">每日一次（推荐）</option>
                  <option value="once_per_session">每次会话一次</option>
                  <option value="always">每次访问都弹</option>
                </select>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>开始时间（可选）</Label>
                <Input type="datetime-local" value={editing.starts_at?.slice(0, 16) || ""}
                  onChange={(e) => setEditing({ ...editing, starts_at: e.target.value ? e.target.value + ":00Z" : null })} />
              </div>
              <div className="space-y-1.5">
                <Label>结束时间（可选）</Label>
                <Input type="datetime-local" value={editing.ends_at?.slice(0, 16) || ""}
                  onChange={(e) => setEditing({ ...editing, ends_at: e.target.value ? e.target.value + ":00Z" : null })} />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
              启用此公告
            </label>

            <div className="flex items-center gap-3 pt-2 border-t">
              <Button onClick={save}><Save className="h-4 w-4 mr-1" /> 保存</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>取消</Button>
              {msg && <span className="text-sm text-destructive">{msg}</span>}
            </div>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">公告管理</h1>
            <Button onClick={() => setEditing({ ...EMPTY })}>
              <Plus className="h-4 w-4 mr-1" /> 新建公告
            </Button>
          </div>

          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无公告。新建后将在前台展示。</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 px-2">位置</th>
                    <th className="py-2 px-2">标题/内容</th>
                    <th className="py-2 px-2">优先级</th>
                    <th className="py-2 px-2">状态</th>
                    <th className="py-2 px-2">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((it) => {
                    const place = PLACEMENTS.find((p) => p.v === it.placement);
                    return (
                      <tr key={it.id} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-2 whitespace-nowrap">{place?.label || it.placement}</td>
                        <td className="py-2 px-2 max-w-md">
                          <div className="truncate font-medium">{it.title || "（无标题）"}</div>
                          <div className="truncate text-xs text-muted-foreground">{it.content_text || it.content_html?.replace(/<[^>]+>/g, "")}</div>
                        </td>
                        <td className="py-2 px-2">{it.priority}</td>
                        <td className="py-2 px-2">
                          {it.is_active
                            ? <span className="inline-flex items-center gap-1 text-emerald-600"><Eye className="h-3.5 w-3.5" /> 启用</span>
                            : <span className="inline-flex items-center gap-1 text-muted-foreground"><EyeOff className="h-3.5 w-3.5" /> 停用</span>}
                        </td>
                        <td className="py-2 px-2 space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ ...EMPTY, ...it })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(it.id!)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
